import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { RouterProvider } from "@tanstack/react-router";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { isConfigured, serviceConfig } from "./config";
import { router } from "./router";
import { getAppProviderMode, resolveProviderPathname } from "./appProviderMode";
import { AppProviderModeContext } from "./appProviderModeContext";
import "./styles.css";

const convexClient = isConfigured(serviceConfig.convex.url)
  ? new ConvexReactClient(serviceConfig.convex.url as string)
  : undefined;

function subscribeToRouterLocation(listener: () => void) {
  const unsubscribeHistory = router.history.subscribe(() => listener());
  const unsubscribeResolved = router.subscribe("onResolved", () => listener());
  const unsubscribeRendered = router.subscribe("onRendered", () => listener());
  return () => {
    unsubscribeHistory();
    unsubscribeResolved();
    unsubscribeRendered();
  };
}

function getRouterPathname() {
  const leafMatch = router.state.matches[router.state.matches.length - 1];
  return resolveProviderPathname(leafMatch?.routeId, router.history.location.pathname);
}

function AppProviders() {
  const providerMode = React.useSyncExternalStore(
    subscribeToRouterLocation,
    () => getAppProviderMode(getRouterPathname()),
    () => "internal" as const,
  );
  const app = (
    <AppProviderModeContext.Provider value={providerMode}>
      <RouterProvider router={router} />
    </AppProviderModeContext.Provider>
  );

  React.useEffect(() => {
    document.documentElement.dataset.appProviderMode = providerMode;
  }, [providerMode]);

  if (providerMode === "public") {
    return convexClient ? <ConvexProvider client={convexClient}>{app}</ConvexProvider> : app;
  }

  if (!serviceConfig.clerk.publishableKey) {
    return convexClient ? <ConvexProvider client={convexClient}>{app}</ConvexProvider> : app;
  }

  if (convexClient) {
    return (
      <ClerkProvider publishableKey={serviceConfig.clerk.publishableKey}>
        <ConvexProviderWithClerk client={convexClient} useAuth={useAuth}>
          {app}
        </ConvexProviderWithClerk>
      </ClerkProvider>
    );
  }

  return <ClerkProvider publishableKey={serviceConfig.clerk.publishableKey}>{app}</ClerkProvider>;
}

const rootElement = document.getElementById("root") as HTMLElement & {
  odcReactRoot?: ReturnType<typeof ReactDOM.createRoot>;
};

const root = rootElement.odcReactRoot ?? ReactDOM.createRoot(rootElement);
rootElement.odcReactRoot = root;

root.render(
  <React.StrictMode>
    <AppProviders />
  </React.StrictMode>,
);
